import axios from 'axios';
import _ from 'lodash';

const defaultPagination = {
  page: 1,
  rowsPerPage: 20,
  sortBy: 'created',
  descending: true,
  total: null,
};

export const state = {
  vaultWallets: [],
  vaultWalletBalances: [],
  search: null,
  vaultWalletsPagination: defaultPagination,
  vaultWalletsNextPage: [],
  loading: false,
};

export const mutations = {
  setVaultWallets(context, vaultWallets) {
    context.vaultWallets = vaultWallets;
    context.loading = false;
  },

  setVaultWalletBalances(context, vaultWalletBalances) {
    context.vaultWalletBalances = vaultWalletBalances;
    context.loading = false;
  },

  updateVaultWalletTokenBalance(context, vaultTokenBalance) {
    context.vaultWalletBalances = context.vaultWalletBalances.map((token) => {
      if (token.name === vaultTokenBalance.currency) {
        token.balance = vaultTokenBalance.balance;
      }

      return token;
    });
  },

  setVaultWalletsNextPage(context, nextUrl) {
    context.vaultWalletsNextPage = [...context.vaultWalletsNextPage, nextUrl];
    context.loading = false;
  },

  clearVaultWalletsNextPage(context) {
    context.vaultWalletsNextPage = [];
    context.loading = false;
  },

  reset(context) {
    context.vaultWallets = [];
    context.vaultWalletsNextPage = [];
    context.vaultWalletsPagination = defaultPagination;
    context.loading = false;
    context.search = null;
  },

  setVaultWalletsBackPage(context) {
    context.vaultWalletsNextPage.pop();
    context.vaultWalletsNextPage.pop();
    context.loading = false;
  },

  setVaultWalletsPagination(context, pagination) {
    context.vaultWalletsPagination = pagination;
    context.loading = false;
  },
  setSearch(context, search) {
    context.search = search;
  },
  setTotalItems(context, value) {
    context.vaultWalletsPagination.total = value;
  },

  setLimitItemsPerPage(context, value) {
    context.vaultWalletsPagination.limit = value;
  },

  setLoading(context, value) {
    context.loading = value;
  },
};

export const getters = {
  getVaultWalletsPagination: currenctState => currenctState.vaultWalletsPagination,
  vaultWalletBalances: currenctState => currenctState.vaultWalletBalances,
};

export const actions = {
  async setLimitItemsPerPage({ commit, dispatch }, limit) {
    commit('setLimitItemsPerPage', limit);
    commit('clearVaultWalletsNextPage');
    dispatch('getVaultWallets');
  },

  async resetVaultWalletStore({ commit }) {
    commit('reset');
  },

  async vaultWalletBackPage({ commit, dispatch }) {
    commit('setVaultWalletsBackPage');
    dispatch('getVaultWallets', true);
  },

  async getVaultWalletBalances({ commit, rootState }, { userId, wallet }) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const url = `${window.BASE_API.VAULT}/users/${userId}/wallets/${wallet}/tokens`;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };

    commit('setLoading', true);
    try {
      const response = await axios.get(url, options);
      commit('setLoading', false);
      const walletBalances = response.data
        .map(({ asset, balance }) => ({
          name: asset.name,
          token: asset.token,
          balance: balance.balance,
          project: asset.project,
        }))
        .filter(item => item.balance > 0 || item.token === userId);
      commit('setVaultWalletBalances', walletBalances);

      return walletBalances;
    } catch (error) {
      commit('setLoading', false);
      commit('setVaultWalletBalances', []);

      return Promise.reject(error);
    }
  },

  async refreshVaultWalletBalances({ commit, rootState }, { userId, wallet }) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const url = `${window.BASE_API.VAULT}/users/${userId}/wallets/${wallet}/tokens/refresh`;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };

    commit('setLoading', true);
    try {
      await axios.post(url, options);
      commit('setLoading', false);
    } catch (error) {
      commit('setLoading', false);
    }
  },

  async refreshVaultWalletTokenBalances({ commit, rootState }, { userId, wallet, token }) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const url = `${window.BASE_API.VAULT}/users/${userId}/wallets/${wallet}/tokens/${token}/refresh`;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };

    commit('setLoading', true);
    try {
      const response = await axios.post(url, options);

      commit('updateVaultWalletTokenBalance', response.data);
      commit('setLoading', false);
    } catch (error) {
      commit('setLoading', false);
    }
  },

  async requeueWallets({ commit, dispatch, rootState }, payload) {
    commit('setErrorOnRequeue', null);
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    const url = `${window.BASE_API.VAULT}/admin/wallets/requeue`;
    try {
      await axios.post(url, {
        wallets: payload,
      }, options);
      commit('setLoading', false);
      dispatch('getVaultWallets');
    } catch (error) {
      commit('setLoading', false);
      throw error;
    }
  },

  async fetchWallet({ commit, rootState, ...context }, search) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    try {
      const searchState = search;
      const { rowsPerPage, sortBy, descending } = context.state.vaultWalletsPagination;
      const url = `${window.BASE_API.VAULT}/admin/wallets`;
      const query = {
        size: rowsPerPage,
        ...(searchState ? {
          query: {
            bool: {
              must: {
                multi_match: {
                  query: searchState,
                  operator: 'and',
                },
              },
            },
          },
        } : null),
        sort: {
          [sortBy || 'created']: {
            order: descending ? 'desc' : 'asc',
          },
        },
      };
      const response = await axios.post(url, query, options);
      const vaultWallets = serializeWallets(response);

      return Promise.resolve(vaultWallets);
    } catch (err) {
      console.error(err);

      return Promise.reject(err);
    }
  },

  async getVaultWallets({ commit, rootState, ...context }, search) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    try {
      commit('setLoading', true);
      if (search) {
        commit('setSearch', search);
      }
      const searchState = context.state.search || search;
      const { page, rowsPerPage, sortBy, descending } = context.state.vaultWalletsPagination;
      const url = `${window.BASE_API.VAULT}/admin/wallets`;
      const query = {
        from: (page - 1) * rowsPerPage,
        size: rowsPerPage,
        ...(searchState ? {
          query: {
            bool: {
              must: {
                multi_match: {
                  query: searchState,
                  operator: 'and',
                },
              },
            },
          },
        } : null),
        sort: {
          [sortBy || 'created']: {
            order: descending ? 'desc' : 'asc',
          },
        },
      };
      const response = await axios.post(url, query, options);
      commit('setTotalItems', _.get(response, 'data.body.hits.total.value'));
      const vaultWallets = serializeWallets(response);
      commit('setVaultWallets', vaultWallets);
      commit('setLoading', false);
    } catch (err) {
      console.error(err);
      commit('setLoading', false);
    }
  },
  async getVaultUser({ rootState }, userId) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const url = `${window.BASE_API.VAULT}/admin/users/${userId}`;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };

    const response = await axios.get(url, options);

    return response.data;
  },

  async requeueVaultUser({ rootState }, userId) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const url = `${window.BASE_API.VAULT}/admin/users/${userId}/requeue`;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };

    const response = await axios.post(url, {}, options);

    return response.data;
  },

  async startProcessingQueuedWallets({ commit, rootState, ...context }) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };

    return axios.get(`${window.BASE_API.VAULT}/admin/walletStartProcessingQueued`, options);
  },

  setVaultWalletsPagination({ commit, dispatch }, pagination) {
    commit('setVaultWalletsPagination', pagination);
    dispatch('getVaultWallets');
  },
};

const serializeWallets = (wallets) => {
  if (!_.has(wallets, 'data.body.hits.hits')) {
    console.error('wrong fetching wallets');

    return [];
  }

  return _.get(wallets, 'data.body.hits.hits').map((wallet) => {
    wallet = wallet._source;
    wallet.created = new Date(wallet.created * 1000);
    wallet.updated = new Date(wallet.updated * 1000);

    return wallet;
  });
};

const vaultWalletsStore = {
  state,
  mutations,
  getters,
  actions,
};

export default vaultWalletsStore;



// WEBPACK FOOTER //
// ./src/store/vault/walletsStore.js